import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import axios from "axios";

/**
 * Advisory-only duplicate check. Always scoped on the server to the current instance.
 * Two matches at first; others are revealed on demand rather than opening a modal.
 */
export default function CustomerDuplicateSuggestions({ instanceUniqueId, businessName, emailAddress, phoneNumber, excludeCustomerId, enabled = true }) {
  const [matches, setMatches] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const searchable = Boolean(instanceUniqueId && enabled && (String(businessName || "").trim().length >= 3 || String(emailAddress || "").trim().length >= 5 || String(phoneNumber || "").replace(/\D/g, "").length >= 7));

  useEffect(() => {
    setExpanded(false);
    if (!searchable) { setMatches([]); setLoading(false); setFailed(false); return; }
    let live = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/customers/possible-duplicates`, {
          params: { instanceUniqueId, businessName, emailAddress, phoneNumber, excludeCustomerId },
          headers: { accessToken: localStorage.getItem("accessToken") },
        });
        if (live) { setMatches(data.matches || []); setFailed(false); }
      } catch (_error) {
        if (live) { setMatches([]); setFailed(true); }
      } finally {
        if (live) setLoading(false);
      }
    }, 400);
    return () => { live = false; clearTimeout(timer); };
  }, [searchable, instanceUniqueId, businessName, emailAddress, phoneNumber, excludeCustomerId]);

  if (!enabled || !searchable) return null;
  return (
    <Box sx={{ mb: 2 }}>
      {loading && <Typography variant="caption" color="text.secondary"><CircularProgress size={12} sx={{ mr: 1 }} />Checking existing customers…</Typography>}
      {failed && <Alert severity="info">Similar customer check unavailable. You may still continue.</Alert>}
      {!loading && !failed && matches.length > 0 && (
        <Box
          role="region"
          aria-label="Possible existing customers"
          sx={{
            p: { xs: 1.5, sm: 2 },
            bgcolor: "#FFF8F2",
            border: "1px solid #F0D7C5",
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(93, 54, 29, 0.05)",
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: 1.5,
                bgcolor: "#FCE8D3",
                color: "#BD570D",
              }}
            >
              <ReportProblemOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#252B37", lineHeight: 1.3 }}>
                  Possible existing customers
                </Typography>
                <Chip
                  size="small"
                  label={(matches.length >= 15 ? "15+" : matches.length) + " matches"}
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    borderRadius: 1.5,
                    bgcolor: "#FCE4D2",
                    color: "#99410D",
                  }}
                />
              </Stack>
              <Typography variant="body2" sx={{ mt: 0.5, color: "#596474", lineHeight: 1.45 }}>
                Review these possible matches before merging or converting. These suggestions do not block you from continuing.
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1} sx={{ mt: 1.75 }}>
            {(expanded ? matches : matches.slice(0, 2)).map((match) => (
              <Box
                key={match.id}
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  px: { xs: 1.5, sm: 2 },
                  py: 1.25,
                  bgcolor: "#FFFFFF",
                  border: "1px solid #E9DCD4",
                  borderRadius: 1.75,
                  boxShadow: "0 1px 3px rgba(35, 38, 45, 0.04)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 42,
                      height: 42,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      bgcolor: "#F3F5F7",
                      color: "#526074",
                    }}
                  >
                    <BusinessOutlinedIcon sx={{ fontSize: 23 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, pl: 1.5, borderLeft: "1px solid #E2E5E9" }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#252B37", lineHeight: 1.35, overflowWrap: "anywhere" }}
                    >
                      {match.businessName || "Unnamed customer"}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: "#64748B" }}>
                      {match.status === "lead" ? "Lead" : match.status === "in-review" ? "Under review" : "Customer"} · {match.strength === "strong" ? "Strong match" : "Possible match"}
                    </Typography>
                    {(match.reasons || []).length > 0 && (
                      <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mt: 0.25, color: "#64748B" }}>
                        <LocalOfferOutlinedIcon sx={{ fontSize: 14, mt: "2px", flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: "inherit", lineHeight: 1.4 }}>
                          {match.reasons.slice(0, 2).join(" · ")}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
                <Button
                  component="a"
                  href={"/instance/" + instanceUniqueId + "/sales/customers?tab=all&openCustomer=" + encodeURIComponent(match.id)}
                  variant="outlined"
                  size="small"
                  sx={{
                    alignSelf: { xs: "flex-start", sm: "center" },
                    flexShrink: 0,
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#253244",
                    borderColor: "#9BA6B6",
                    bgcolor: "#FFFFFF",
                    "&:hover": { borderColor: "#526074", bgcolor: "#F8FAFC" },
                  }}
                >
                  View match
                </Button>
              </Box>
            ))}
          </Stack>

          {matches.length > 2 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowRightIcon />}
              sx={{
                mt: 1.5,
                textTransform: "none",
                fontWeight: 700,
                borderColor: "#C81020",
                color: "#C81020",
                "&:hover": { borderColor: "#A60E1B", bgcolor: "#FFF0EF" },
              }}
            >
              {expanded ? "Show fewer" : "View all " + (matches.length >= 15 ? "15+" : matches.length) + " matches"}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
